/**
 * Keeping a long debate inside an 8K window.
 *
 * Free's design: "preserve the intro and start to compress/summarize oldest
 * messages as needed. Maybe thinks get shrunk down into a small 1 or 2 sentence
 * gist that is a 'past thoughts' block that stays in line and also ran through
 * compression."
 *
 * WHY THIS SHAPE. By the third rebuttal an agent's context is intro, staging
 * note, question, its own note, its own answer, the opponent's answer, its
 * rebuttal note, its rebuttal, and so on. The parts do not age equally:
 *
 *   - The INTRO never goes. It carries who the agent is and who it is arguing
 *     with, and losing it loses the debate's frame.
 *   - What was SAID is the debate. Dropping an answer would make a rebuttal
 *     reply to something absent.
 *   - THINK BLOCKS have already done their work. Each one shaped a retrieval
 *     and an answer that are both still present, so a note is the cheapest
 *     thing to lose and the safest thing to compress.
 *
 * So the eviction order is fixed: oldest notes first, folded into a rolling
 * "past thoughts" gist that itself gets recompressed as it grows. Nothing that
 * was spoken aloud is ever summarised, because a debate transcript that quietly
 * paraphrases what someone said is worse than one that runs out of room.
 *
 * This module decides WHAT to compress. The actual summarising is a model call
 * and lives with the orchestration; every call in this protocol is a one-shot,
 * so the extra latency is small.
 */

export type Role = "system" | "user" | "assistant";

export type BudgetedMsg = {
  role: Role;
  content: string;
  /**
   * What this message is, which is what decides whether it can be dropped.
   * `intro` is pinned, `spoken` is never summarised, `note` is compressible,
   * `gist` is an already-compressed run of notes.
   */
  kind: "intro" | "spoken" | "note" | "gist" | "facts";
};

/**
 * Rough token count.
 *
 * Four characters per token is the usual English approximation and it is close
 * enough for a budget that exists to trigger compression EARLY. Being wrong in
 * the cautious direction costs a little context; being wrong the other way
 * costs a failed generation mid-debate.
 */
export function estimateTokens(text: string): number {
  return Math.ceil(text.length / 4);
}

export function totalTokens(msgs: BudgetedMsg[]): number {
  // Four tokens per message for role delimiters, which the chat template adds.
  return msgs.reduce((n, m) => n + estimateTokens(m.content) + 4, 0);
}

export type BudgetPlan = {
  /** Indices of notes to fold into the gist, oldest first. */
  compress: number[];
  /** True when even compressing every note leaves us over budget. */
  stillOver: boolean;
  before: number;
  /** Projected total once the chosen notes become a gist of `gistTokens`. */
  after: number;
};

/**
 * Which notes to fold, to get under `limit`.
 *
 * Chooses the FEWEST oldest notes that do the job, rather than compressing
 * everything the moment the budget is touched. A note that is still in the
 * window is worth more intact than gisted, so it is kept until its space is
 * actually needed.
 *
 * An EXISTING GIST is always folded in alongside them. Free's design says the
 * past-thoughts block is "also ran through compression", and rewriting it is how
 * that happens: the old gist and the new notes go into one call and come back as
 * one or two sentences, so the block stays the same size no matter how many
 * exchanges it has absorbed. Leaving it in place instead would duplicate its
 * content inside its own successor.
 */
export function planCompression(
  msgs: BudgetedMsg[],
  limit: number,
  gistTokensPerNote = 24,
): BudgetPlan {
  const before = totalTokens(msgs);
  if (before <= limit) return { compress: [], stillOver: false, before, after: before };

  // Every note EXCEPT the newest.
  //
  // The trailing note is the prefill: the thought this agent just had, about the
  // turn it is about to take, which has not done its work yet. The whole reason
  // notes are the cheapest thing to fold is that each one already shaped a
  // retrieval and an answer that both survive. That is not true of this one.
  // Folding it makes the answer continue from a summary of OLD thinking instead
  // of the thought it just had, which is the exact condition under which a
  // speaker reaches for the opponent's words instead of its own.
  //
  // It only bites under heavy pressure, which is why the control arm never
  // showed it and reading the code did.
  const lastIdx = msgs.length - 1;
  const noteIdx = msgs
    .map((m, i) => ({ m, i }))
    .filter((x) => x.m.kind === "note" && x.i !== lastIdx)
    .map((x) => x.i);

  const compress: number[] = [];
  let projected = before;
  for (const i of noteIdx) {
    // Replacing a note with its share of a gist reclaims the difference.
    projected -= Math.max(0, estimateTokens(msgs[i].content) - gistTokensPerNote);
    compress.push(i);
    if (projected <= limit) break;
  }

  // Fold the standing gist in too, but only once there is other work to do.
  // Rewriting it on its own would spend a model call to say the same thing again.
  if (compress.length > 0) {
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].kind === "gist") compress.unshift(i);
    }
    compress.sort((a, b) => a - b);
  }

  return {
    compress,
    stillOver: projected > limit,
    before,
    after: projected,
  };
}

/**
 * The last resort: drop the oldest complete exchange.
 *
 * Reached only when every note has already been folded and the context is STILL
 * too long. Dropping is not summarising, and the distinction is the point. A
 * paraphrase of what someone said would let a rebuttal answer words nobody used;
 * an exchange that has fallen out of the window is simply gone, and what remains
 * is still verbatim.
 *
 * Takes a whole user/assistant pair so the alternation survives, and never
 * touches the intro. Returns the input unchanged when there is nothing left to
 * give, so a caller cannot loop forever on it.
 */
/**
 * Give up the memory banner. The very last lever, pulled after everything else.
 *
 * WHY LAST, not first. The retrieved facts are the only thing in the context
 * that was neither spoken nor thought, and they cost one search to get back, so
 * they look like the cheapest thing to lose. They are not: they are what the
 * answer is grounded in, and this whole bay exists to argue that grounding comes
 * from the lattice. Trading them away to keep three-exchange-old speech would be
 * trading the thesis for the transcript.
 *
 * So the order is: fold the notes, drop the oldest speech, and only when both
 * are exhausted, answer ungrounded rather than not at all.
 *
 * Dropped whole. A half-banner cut mid-fact reads as a fact the lattice does not
 * actually hold, which is the one failure mode worse than having no banner.
 */
export function dropFacts(msgs: BudgetedMsg[]): BudgetedMsg[] {
  const out = msgs.filter((m) => m.kind !== "facts");
  return out.length === msgs.length ? msgs : out;
}

export function dropOldestExchange(msgs: BudgetedMsg[]): BudgetedMsg[] {
  const first = msgs.findIndex((m) => m.kind === "spoken");
  if (first < 0) return msgs;
  // Never drop the newest turn. A single message too large for the window is a
  // real situation (one enormous pasted question), and answering it truncated
  // beats answering nothing at all.
  if (first === msgs.length - 1) return msgs;
  // Take this message and the next spoken one, so a turn and its reply leave
  // together and the roles still alternate afterwards.
  const second = msgs.findIndex((m, i) => i > first && m.kind === "spoken");
  const drop = new Set(second >= 0 ? [first, second] : [first]);
  const out = msgs.filter((_, i) => !drop.has(i));
  return out.length === msgs.length ? msgs : out;
}

/**
 * Apply a plan: the chosen notes leave, and one gist message stands in for them.
 *
 * The gist is placed where the OLDEST compressed note was, so the conversation
 * keeps its shape and the summary sits at the point in time it summarises.
 * Its role follows that note's role, because a gist of the agent's own thinking
 * is still the agent's own thinking and must not become a user turn.
 */
export function applyCompression(
  msgs: BudgetedMsg[],
  plan: BudgetPlan,
  gistText: string,
): BudgetedMsg[] {
  if (plan.compress.length === 0) return msgs;
  const drop = new Set(plan.compress);
  const anchor = Math.min(...plan.compress);
  const out: BudgetedMsg[] = [];
  for (let i = 0; i < msgs.length; i++) {
    if (i === anchor) {
      out.push({ role: msgs[i].role, content: gistText, kind: "gist" });
      continue;
    }
    if (drop.has(i)) continue;
    out.push(msgs[i]);
  }
  return out;
}

/** The notes a plan wants folded, in order, ready to be summarised. */
export function notesToCompress(msgs: BudgetedMsg[], plan: BudgetPlan): string[] {
  return plan.compress.map((i) => msgs[i].content);
}

/**
 * The prompt that makes the gist.
 *
 * Asks for the agent's own voice in the first person, because the result is
 * spliced back in as the agent's own turn. A third-person summary ("the agent
 * considered...") reads as somebody describing it, which is the same category
 * error that made a prefilled note get answered rather than continued.
 */
export function gistPrompt(notes: string[], existingGist?: string): string {
  const parts = [
    existingGist ? `Your earlier thinking, already condensed:\n\n${existingGist}\n` : "",
    `Thoughts to condense:\n`,
    ...notes.map((n, i) => `${i + 1}. ${n}`),
    ``,
    `Condense all of that into one or two sentences, in your own voice, first`,
    `person, keeping only what still bears on the argument. No preamble.`,
  ];
  return parts.filter(Boolean).join("\n");
}

/** Cap the gist so a "compressed" block cannot grow without limit. */
export function clampGist(text: string, maxChars = 320): string {
  const t = text.trim().replace(/\s+/g, " ");
  if (t.length <= maxChars) return t;
  const cut = t.slice(0, maxChars);
  const stop = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  return (stop > maxChars * 0.5 ? cut.slice(0, stop + 1) : cut).trim();
}
